import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { ExternalLink, ArrowLeft, Flag } from 'lucide-react'
import { useToast } from './ui/use-toast'

interface Job {
  id: string
  job_title: string
  job_url: string | null
  job_posted_at: string
  ats_type: string | null
  company_name: string
  is_active: boolean | string | number | null
}

interface CompanyInfo {
  company_name: string
  logo_url: string | null
  description: string | null
  website_url: string | null
  employee_range: string | null
  funding_stage: string | null
  headquarters: string | null
  industry: string | null
}

function CompanyPage() {
  const { companyName } = useParams<{ companyName: string }>()
  const [jobs, setJobs] = useState<Job[]>([])
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [logoFailed, setLogoFailed] = useState(false)
  const [reportedCompany, setReportedCompany] = useState(false)
  const [reportingCompany, setReportingCompany] = useState(false)
  const { toast } = useToast()

  // Get the slug from URL
  const companySlug = companyName ? decodeURIComponent(companyName) : ''

  useEffect(() => {
    fetchCompanyData()
  }, [companyName])

  async function fetchCompanyData() {
    try {
      setLoading(true)

      // Fetch company info from companies table using slug
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('slug, name, logo_url, description, website_url, employee_range, funding_stage, headquarters, industry')
        .eq('slug', companySlug)
        .maybeSingle()

      if (companyError) {
        console.error('Error fetching company info:', companyError)
      }

      if (companyData) {
        console.log('Company data:', companyData)
        setCompanyInfo({
          company_name: companyData.name,
          logo_url: companyData.logo_url,
          description: companyData.description,
          website_url: companyData.website_url,
          employee_range: companyData.employee_range,
          funding_stage: companyData.funding_stage,
          headquarters: companyData.headquarters,
          industry: companyData.industry
        })

        // Fetch all jobs for this company from raw_jobs using the slug
        console.log('Querying raw_jobs with company_name (slug):', companyData.slug)
        const { data: jobsData, error: jobsError } = await supabase
          .from('raw_jobs')
          .select('id, job_title, job_url, job_posted_at, ats_type, company_name, is_active')
          .eq('company_name', companyData.slug)
          .order('job_posted_at', { ascending: false })

        console.log('Jobs found:', jobsData?.length || 0, jobsData)

        // Debug: Check is_active types
        if (jobsData && jobsData.length > 0) {
          console.log('First job is_active value:', jobsData[0].is_active, 'Type:', typeof jobsData[0].is_active)
        }

        if (jobsError) {
          console.error('Error fetching jobs:', jobsError)
          throw jobsError
        }

        setJobs(jobsData || [])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch company data')
      console.error('Error:', err)
    } finally {
      setLoading(false)
    }
  }

  function getCompanyInitials(companyName: string): string {
    const words = companyName.trim().split(/\s+/)
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase()
    }
    return (words[0][0] + words[1][0]).toUpperCase()
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  function formatFundingStage(stage: string | null): string {
    if (!stage || stage.toLowerCase() === 'unknown') return ''

    if (stage.toLowerCase().startsWith('series')) {
      const parts = stage.split('-')
      if (parts.length === 2) {
        return `Series ${parts[1].toUpperCase()}`
      }
    }

    return stage.charAt(0).toUpperCase() + stage.slice(1).toLowerCase()
  }

  function isJobActive(job: Job): boolean {
    // Handle different types that might come from Supabase
    const isActive = job.is_active

    // If it's already a boolean, return it
    if (typeof isActive === 'boolean') {
      return isActive
    }

    // Handle string values
    if (typeof isActive === 'string') {
      return isActive.toLowerCase() === 'true' || isActive === '1'
    }

    // Handle number values
    if (typeof isActive === 'number') {
      return isActive === 1
    }

    // Default to false if null or undefined
    return false
  }

  function getATSUrl(job: Job): string | null {
    // If job has a URL, use it
    if (job.job_url) return job.job_url

    // Otherwise, generate fallback URL based on ATS type
    if (!job.ats_type || !job.company_name) return null

    const atsType = job.ats_type.toLowerCase()
    const companySlug = job.company_name

    switch (atsType) {
      case 'ashby':
        return `https://jobs.ashbyhq.com/${companySlug}`
      case 'lever':
        return `https://jobs.lever.co/${companySlug}`
      case 'greenhouse':
        return `https://job-boards.greenhouse.io/${companySlug}`
      default:
        return null
    }
  }

  // Handle reporting incorrect company data
  async function handleReportCompany() {
    // Prevent duplicate submissions
    if (reportedCompany || reportingCompany || !companyInfo) {
      return
    }

    setReportingCompany(true)

    try {
      const response = await fetch('/api/report-company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId: companySlug,
          companyName: companyInfo.company_name,
          websiteUrl: companyInfo.website_url,
        })
      })

      if (response.ok) {
        setReportedCompany(true)
        toast({
          description: "Thanks! We'll take it from here 🫡",
        })
      } else {
        toast({
          title: "Failed to submit report",
          description: "Please try again later.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error reporting company:', error)
      toast({
        title: "Failed to submit report",
        description: "Please try again later.",
        variant: "destructive",
      })
    } finally {
      setReportingCompany(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white pb-8">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-32 bg-gray-200 rounded-lg mb-8"></div>
            <div className="h-96 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white pb-8">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            Error: {error}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white pb-8">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Back Button */}
        <Link
          to="/company/directory"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Companies
        </Link>

        {/* Company Header */}
        <div className="mb-8 pb-8">
          <div className="flex items-start gap-4 mb-4">
            {/* Logo */}
            {companyInfo?.logo_url && !logoFailed ? (
              <img
                src={companyInfo.logo_url}
                alt={companyInfo.company_name}
                className="w-20 h-20 rounded-lg object-contain bg-white shrink-0"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div className="w-20 h-20 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 text-white text-xl font-semibold shrink-0">
                {getCompanyInitials(companySlug)}
              </div>
            )}

            {/* Company Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">
                  {companyInfo?.company_name || companySlug}
                </h1>
                {companyInfo?.industry && companyInfo.industry.toLowerCase() !== 'unknown' && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                    {companyInfo.industry}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-gray-600 items-center justify-between">
                <div className="flex flex-wrap gap-4">
                  {companyInfo?.headquarters && companyInfo.headquarters.toLowerCase() !== 'unknown' && (
                    <span>{companyInfo.headquarters}</span>
                  )}
                  {companyInfo?.employee_range && companyInfo.employee_range.toLowerCase() !== 'unknown' && (
                    <span>{companyInfo.employee_range} employees</span>
                  )}
                  {companyInfo?.funding_stage && formatFundingStage(companyInfo.funding_stage) && (
                    <span>{formatFundingStage(companyInfo.funding_stage)}</span>
                  )}
                </div>
                {/* Website Link */}
                {companyInfo?.website_url && (
                  <a
                    href={companyInfo.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 transition-colors text-sm font-medium"
                  >
                    website
                  </a>
                )}
              </div>
            </div>
          </div>

          {companyInfo?.description && (
            <p className="text-gray-600 leading-relaxed">
              {companyInfo.description}
            </p>
          )}

          {/* Report Incorrect Data */}
          <div className="mt-4 pt-4 border-t border-gray-100 text-right">
            <button
              onClick={handleReportCompany}
              disabled={reportedCompany || reportingCompany}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Flag className="w-3 h-3" />
              {reportedCompany
                ? 'report submitted'
                : reportingCompany
                ? 'submitting...'
                : 'report incorrect company data'}
            </button>
          </div>
        </div>

        {/* Jobs Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Job History ({jobs.length})
          </h2>

          {jobs.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-600">
              No open positions at this time.
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Title
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Posted
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {jobs.map((job) => {
                    const applyUrl = getATSUrl(job)
                    return (
                      <tr
                        key={job.id}
                        onClick={() => applyUrl && window.open(applyUrl, '_blank')}
                        className={`transition-colors ${applyUrl ? 'cursor-pointer hover:bg-blue-50' : 'cursor-default hover:bg-gray-50'}`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {job.job_title}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 text-center">
                          {formatDate(job.job_posted_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-center">
                          {isJobActive(job) ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Inactive
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CompanyPage
